import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { Modal } from '@/components/ui/Modal'
import type { EmailTemplate } from './EmailTemplatesTab'

interface EmailPreviewModalProps {
  open: boolean
  template: EmailTemplate | null
  onClose: () => void
}

interface PreviewData {
  subject: string
  html: string
  text: string
  isSampleData?: boolean
}

export function EmailPreviewModal({ open, template, onClose }: EmailPreviewModalProps) {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<PreviewData | null>(null)

  useEffect(() => {
    if (!open || !template) {
      setPreview(null)
      return
    }

    let cancelled = false
    setLoading(true)
    void api
      .get(`/email/templates/${template.id}/preview`)
      .then(({ data }) => {
        if (!cancelled) setPreview(data.data ?? null)
      })
      .catch(() => {
        if (!cancelled) toast.error('Erro ao gerar preview do e-mail')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, template])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={template ? `Preview — ${template.nome}` : 'Preview do e-mail'}
      description="Visualização com dados de exemplo"
      size="3xl"
    >
      {loading ? (
        <div className="flex items-center justify-center py-16 text-text-muted">
          <Loader2 size={24} className="animate-spin text-accent" />
        </div>
      ) : preview ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-[var(--glass-border)] bg-white/[0.03] px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-label mb-1">Assunto</p>
            <p className="text-sm text-text-primary font-medium break-words">{preview.subject}</p>
          </div>

          <div className="overflow-hidden rounded-lg border border-[var(--glass-border)] bg-white">
            <iframe
              title="Preview do e-mail"
              srcDoc={preview.html}
              className="w-full min-h-[420px] border-0 bg-white"
              sandbox=""
            />
          </div>

          <p className="text-[11px] text-text-muted text-center">
            Dados fictícios para visualização — o e-mail real usará informações do CRM no disparo.
          </p>
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-text-muted">Não foi possível carregar o preview.</p>
      )}
    </Modal>
  )
}
